import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * DataCard - Unified Card Component für alle Admin Views
 * 
 * Varianten:
 * - default: Standard-Karte mit allen Features
 * - compact: Kompakte Variante für Listen
 * - highlighted: Hervorgehobene Karte (z.B. für aktive Sessions)
 * 
 * Usage:
 * <DataCard
 *   variant="default"
 *   icon="📚"
 *   color="#3B82F6"
 *   title="JavaScript Grundlagen"
 *   subtitle="5 Vorlagen • 3 aktive Sessions"
 *   badges={[{ label: "Aktiv", variant: "success" }]}
 *   stats={[{ icon: "📝", value: 5, label: "Vorlagen" }]}
 *   primaryAction={{ label: "Öffnen", onClick: () => {} }}
 *   menuActions={[{ label: "Bearbeiten", onClick: () => {} }]}
 *   onClick={() => navigate('/course/123')}
 * />
 */

// Menu Dropdown Component
function CardMenu({ actions, isOpen, onToggle, onClose }) {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg theme-transition"
        title="Mehr Optionen"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
        </svg>
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20 py-1">
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
                onClose();
              }}
              className={`w-full flex items-center gap-2 px-4 py-2 text-sm theme-transition ${
                action.variant === 'danger'
                  ? 'text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20'
                  : action.variant === 'warning'
                  ? 'text-warning-600 dark:text-warning-400 hover:bg-warning-50 dark:hover:bg-warning-900/20'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {action.icon && <span>{action.icon}</span>}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

CardMenu.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    variant: PropTypes.oneOf(['default', 'danger', 'warning'])
  })).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

// Badge Component
function CardBadge({ label, variant = 'default', pulse = false }) {
  const variantClasses = {
    default: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    primary: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
    success: 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300',
    warning: 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300',
    danger: 'bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300'
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${variantClasses[variant]}`}>
      {pulse && (
        <span className="w-2 h-2 bg-current rounded-full animate-pulse" />
      )}
      {label}
    </span>
  );
}

CardBadge.propTypes = {
  label: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['default', 'primary', 'success', 'warning', 'danger']),
  pulse: PropTypes.bool
};

// Main DataCard Component
export default function DataCard({
  // Visual
  variant = 'default',
  color,
  icon,
  
  // Content
  title,
  subtitle,
  description,
  
  // Metadata
  badges = [],
  stats = [],
  metadata,
  
  // Actions
  primaryAction,
  secondaryAction,
  menuActions = [],
  actions = [],
  
  // Interaction
  onClick,
  selectable = false,
  selected = false,
  
  className = ''
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  const isClickable = onClick || selectable;
  
  // Base card classes
  const baseClasses = `
    bg-white dark:bg-gray-800 
    rounded-lg shadow-sm 
    border border-gray-200 dark:border-gray-700
    transition-all duration-200
    ${isClickable ? 'cursor-pointer hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600' : ''}
    ${selected ? 'ring-2 ring-primary-500' : ''}
  `;

  // Variant-specific styles
  const variantClasses = {
    default: '',
    compact: '',
    highlighted: 'border-l-4 border-l-danger-500'
  };

  // Color accent (left border or icon background)
  const colorStyle = color && variant !== 'highlighted' ? { borderLeftColor: color, borderLeftWidth: '4px' } : {};

  const handleCardClick = (e) => {
    // Don't trigger card click if clicking on buttons
    if (e.target.closest('button')) return;
    onClick?.();
  };

  if (variant === 'compact') {
    return (
      <div
        onClick={handleCardClick}
        className={`${baseClasses} p-3 flex items-center gap-3 ${className}`}
        style={colorStyle}
      >
        {/* Icon */}
        {icon && (
          <span className="text-2xl flex-shrink-0">{icon}</span>
        )}
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {title}
            </h3>
            {badges.map((badge, index) => (
              <CardBadge key={index} {...badge} />
            ))}
          </div>
          {subtitle && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {subtitle}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {menuActions.length > 0 && (
            <CardMenu
              actions={menuActions}
              isOpen={menuOpen}
              onToggle={() => setMenuOpen(!menuOpen)}
              onClose={() => setMenuOpen(false)}
            />
          )}
          {isClickable && (
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
        </div>
      </div>
    );
  }

  // Default & Highlighted variants
  return (
    <div
      onClick={handleCardClick}
      className={`${baseClasses} ${variantClasses[variant]} p-4 ${className}`}
      style={colorStyle}
    >
      {/* Header: Icon + Title + Menu */}
      <div className="flex items-start gap-3 mb-3">
        {/* Icon */}
        {icon && (
          <span className="text-3xl flex-shrink-0">{icon}</span>
        )}
        
        {/* Title & Subtitle */}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
            {title}
          </h3>
          {subtitle && (
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {subtitle}
            </p>
          )}
        </div>

        {/* Menu */}
        {menuActions.length > 0 && (
          <CardMenu
            actions={menuActions}
            isOpen={menuOpen}
            onToggle={() => setMenuOpen(!menuOpen)}
            onClose={() => setMenuOpen(false)}
          />
        )}
      </div>

      {/* Description */}
      {description && (
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
          {description}
        </p>
      )}

      {/* Badges */}
      {badges.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {badges.map((badge, index) => (
            <CardBadge key={index} {...badge} />
          ))}
        </div>
      )}

      {/* Stats */}
      {stats.length > 0 && (
        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
          {stats.map((stat, index) => (
            <div key={index} className="flex items-center gap-1">
              {stat.icon && <span>{stat.icon}</span>}
              <span className="font-medium">{stat.value}</span>
              {stat.label && <span>{stat.label}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Metadata */}
      {metadata && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
          {metadata}
        </p>
      )}

      {/* Actions */}
      {(primaryAction || secondaryAction || actions.length > 0) && (
        <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          {/* Custom actions array */}
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg theme-transition ${
                action.variant === 'danger'
                  ? 'text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20'
                  : action.variant === 'primary'
                  ? 'text-white bg-primary-600 hover:bg-primary-700'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {action.label}
            </button>
          ))}
          
          {/* Secondary Action */}
          {secondaryAction && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                secondaryAction.onClick();
              }}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg theme-transition"
            >
              {secondaryAction.label}
            </button>
          )}
          
          {/* Primary Action */}
          {primaryAction && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                primaryAction.onClick();
              }}
              disabled={primaryAction.disabled}
              className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg theme-transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {primaryAction.icon && <span className="mr-1">{primaryAction.icon}</span>}
              {primaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

DataCard.propTypes = {
  // Visual
  variant: PropTypes.oneOf(['default', 'compact', 'highlighted']),
  color: PropTypes.string,
  icon: PropTypes.string,
  
  // Content
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  description: PropTypes.string,
  
  // Metadata
  badges: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    variant: PropTypes.oneOf(['default', 'primary', 'success', 'warning', 'danger']),
    pulse: PropTypes.bool
  })),
  stats: PropTypes.arrayOf(PropTypes.shape({
    icon: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string
  })),
  metadata: PropTypes.string,
  
  // Actions
  primaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool
  }),
  secondaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired
  }),
  menuActions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    variant: PropTypes.oneOf(['default', 'danger', 'warning'])
  })),
  actions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    variant: PropTypes.oneOf(['default', 'primary', 'danger'])
  })),
  
  // Interaction
  onClick: PropTypes.func,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  
  className: PropTypes.string
};
